import { useEffect, useId, useState } from 'react'
import { Button } from '@cannect/new-components/atoms'
import { CANCEL_REASONS } from '@cannect/services/resources/recurrency/constants'
import { useCancelSubscription } from '@cannect/services/resources/recurrency/hooks'
import {
  buildCancelReason,
  CANCEL_REASON_MAX,
  CANCEL_REASON_SEPARATOR
} from '@cannect/services/resources/recurrency/helpers'
import type { SubscriptionDetail } from '@cannect/services/resources/recurrency/types'
import { subscriptionsCopy } from '../copy'
import { useSubscriptionAction } from '../hooks/useSubscriptionAction'
import { DialogShell } from './DialogShell'

const copy = subscriptionsCopy.cancel

type Props = {
  detail: SubscriptionDetail
  open: boolean
  onOpenChange: (open: boolean) => void
  onSwitchTo: (dialog: 'pause' | 'postpone') => void
}

// Cancelamento em 2 cliques (botão da barra + confirmar aqui), com retenção.
export const CancelDialog = ({ detail, open, onOpenChange, onSwitchTo }: Props) => {
  const [reason, setReason] = useState('')
  const [comment, setComment] = useState('')
  const reasonId = useId()
  const commentId = useId()

  useEffect(() => {
    if (!open) {
      setReason('')
      setComment('')
    }
  }, [open])

  const label = CANCEL_REASONS.find((item) => item.value === reason)?.label || ''
  const limit = CANCEL_REASON_MAX - (label ? label.length + CANCEL_REASON_SEPARATOR.length : 0)

  const action = useSubscriptionAction({
    detail,
    mutation: useCancelSubscription(detail.id),
    event: 'recurrency_cancel',
    eventPayload: () => (reason ? { reason } : {})
  })

  const showPause = detail.status === 'active' && detail.canPause
  const showPostpone = detail.status === 'active' && detail.canPostpone

  return (
    <DialogShell
      open={open}
      onOpenChange={onOpenChange}
      title={copy.title}
      footer={
        <>
          <Button variant="outline_light" onClick={() => onOpenChange(false)}>
            {subscriptionsCopy.buttons.back}
          </Button>
          <Button
            className="bg-red-700 text-white hover:bg-red-700/90"
            isLoading={action.isPending}
            onClick={async () => {
              const body = { reason: buildCancelReason(label, comment) }
              if (await action.run(body.reason ? body : {})) onOpenChange(false)
            }}>
            {copy.confirm}
          </Button>
        </>
      }>
      {(showPause || showPostpone) && (
        <div className="flex flex-col gap-2 rounded-lg bg-primary-50 p-3">
          <p className="m-0 text-sm font-semibold text-neutral-800">{copy.retention}</p>
          <div className="flex flex-wrap gap-2">
            {showPause && (
              <Button variant="outline_light" size="md" onClick={() => onSwitchTo('pause')}>
                {subscriptionsCopy.buttons.pause}
              </Button>
            )}
            {showPostpone && (
              <Button variant="outline_light" size="md" onClick={() => onSwitchTo('postpone')}>
                {subscriptionsCopy.buttons.postpone}
              </Button>
            )}
          </div>
        </div>
      )}

      <div className="flex flex-col gap-1">
        <label htmlFor={reasonId} className="text-sm font-semibold text-neutral-800">
          {copy.reason}
        </label>
        <select
          id={reasonId}
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          className="h-11 rounded-md border border-solid border-neutral-300 bg-white px-3 text-sm text-neutral-800">
          <option value="">{copy.reasonPlaceholder}</option>
          {CANCEL_REASONS.map((item) => (
            <option key={item.value} value={item.value}>
              {item.label}
            </option>
          ))}
        </select>
      </div>

      <div className="flex flex-col gap-1">
        <label htmlFor={commentId} className="text-sm font-semibold text-neutral-800">
          {copy.comment}
        </label>
        <textarea
          id={commentId}
          value={comment}
          maxLength={limit}
          rows={3}
          onChange={(event) => setComment(event.target.value.slice(0, limit))}
          className="rounded-md border border-solid border-neutral-300 p-3 text-sm text-neutral-800"
        />
        <span className="text-xs text-neutral-600">{copy.remaining(Math.max(0, limit - comment.length))}</span>
      </div>
    </DialogShell>
  )
}
